import { Extension, type Editor } from '@tiptap/react';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { Plugin, PluginKey, TextSelection, type EditorState, type Selection } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

import { createAutocorrectPlugin } from './document-autocorrect';
import { clampRange, changedRanges, textblocksIn, wholeDocument, type Range } from './doc-ranges';
import { getBrowserSpellCheckEngine, type SpellCheckEngine } from './spell-check-engine';
import { MAX_WORDS_PER_CHECK, normaliseWord } from './spell-check-protocol';
import { blockWordTokens } from './spell-tokenize';

/**
 * Word-style spell checking. Misspelled words get a view-only wavy underline (class `spell-error`);
 * decorations are never part of the document, so they are not saved, shared through live editing or
 * recorded as tracked changes. Read-only editors show none. The dictionary runs in a Web Worker and is
 * only loaded once there is something to check, so typing never waits for it.
 */
export const SPELL_CHECK_EXTENSION = 'documentSpellCheck';
export const SPELL_CHECK_DEFAULT_DEBOUNCE_MS = 350;

export interface SpellingIssue { from: number; to: number; word: string }

export type SpellCheckMeta =
  | { type: 'setEnabled'; enabled: boolean }
  /** Re-render pending blocks whose words all have a verdict. */
  | { type: 'refresh' }
  | { type: 'rescanAll' }
  | { type: 'removeWord'; word: string }
  /** The person's own dictionary changed: drop underlines for accepted words, recheck when words were removed. */
  | { type: 'acceptedWordsChanged'; rescan: boolean }
  /** The engine failed: stop checking quietly until the next refresh. */
  | { type: 'halt' };

interface SpellCheckState {
  enabled: boolean;
  decorations: DecorationSet;
  /** Ranges whose text blocks still need checking. */
  pending: readonly Range[];
  /** The word being typed at the caret: underlined once the caret leaves it. */
  deferred: Range | null;
}

export interface SpellCheckController {
  engine: () => SpellCheckEngine | null;
  /** Word -> correct? Cleared by refreshSpelling. */
  verdicts: Map<string, boolean>;
  failed: boolean;
}

export interface SpellCheckOptions {
  enabled: boolean;
  engine: () => SpellCheckEngine | null;
  debounceMs: number;
  /** Fix obvious typos automatically when a word is finished (undoable; follows the spell-check switch). */
  autocorrect: boolean;
}

interface SpellCheckStorage { controller: SpellCheckController }

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    documentSpellCheck: {
      setSpellCheckEnabled: (enabled: boolean) => ReturnType;
      /** Forget cached verdicts and check the whole document again. */
      refreshSpelling: () => ReturnType;
      /** Replace exactly the flagged word, in one transaction, if it is still there. */
      replaceMisspelling: (issue: SpellingIssue, replacement: string) => ReturnType;
    };
  }
}

export const spellCheckKey = new PluginKey<SpellCheckState>('documentSpellCheck');

function spellingKey(word: string): string { return normaliseWord(word).toLowerCase(); }

/* Session-wide "Ignore": applies to every editor on the page until it is reloaded. */
const sessionIgnoredWords = new Set<string>();
const ignoreListeners = new Set<(word: string) => void>();

export function ignoreSpellingWord(word: string): void {
  const key = spellingKey(word);
  if (!key || sessionIgnoredWords.has(key)) return;
  sessionIgnoredWords.add(key);
  for (const listener of ignoreListeners) listener(key);
}

export function isSpellingWordIgnored(word: string): boolean {
  return sessionIgnoredWords.has(spellingKey(word));
}

/** Clears session-only choices (used by tests and on sign-out). */
export function resetIgnoredSpellingWords(): void { sessionIgnoredWords.clear(); }

/* The signed-in person's own dictionary (normalised, lower-case words), shared by every editor on the page. */
const personalWords = new Set<string>();
const personalListeners = new Set<(rescan: boolean) => void>();

/** Replaces the person's dictionary words; editors update their underlines straight away. */
export function setPersonalSpellingWords(words: Iterable<string>): void {
  const next = new Set<string>();
  for (const word of words) { const key = spellingKey(word); if (key) next.add(key); }
  const added = [...next].some(word => !personalWords.has(word));
  const removed = [...personalWords].some(word => !next.has(word));
  if (!added && !removed) return;
  personalWords.clear();
  for (const word of next) personalWords.add(word);
  for (const listener of personalListeners) listener(removed);
}

export function isPersonalSpellingWord(word: string): boolean {
  const key = spellingKey(word);
  if (personalWords.has(key)) return true;
  // Possessives of a personal word (Kubernetes's) are accepted too.
  return /'s$/.test(key) && key.length > 2 && personalWords.has(key.slice(0, -2));
}

/** Ignored for this page session or in the person's own dictionary. */
export function isSpellingWordAccepted(word: string): boolean {
  return isSpellingWordIgnored(word) || isPersonalSpellingWord(word);
}

const MAX_PENDING_RANGES = 64;

function mergePending(doc: ProseMirrorNode, ranges: readonly Range[]): readonly Range[] {
  if (ranges.length <= MAX_PENDING_RANGES) return ranges;
  const from = Math.min(...ranges.map(range => range.from));
  const to = Math.max(...ranges.map(range => range.to));
  return [clampRange(doc, { from: from + 1, to: to - 1 })];
}

function caretInside(selection: Selection, range: Range): boolean {
  return selection.empty && selection.from > range.from && selection.from <= range.to;
}

function spellingDecoration(from: number, to: number, word: string): Decoration {
  return Decoration.inline(from, to, { class: 'spell-error', 'data-spell-word': word }, { spellWord: word, spellKind: 'spelling' });
}

/** Unknown words (no verdict yet) in pending blocks, bounded per worker request. */
export function wordsAwaitingVerdict(state: EditorState, controller: SpellCheckController): string[] {
  const value = spellCheckKey.getState(state);
  if (!value) return [];
  const words = new Set<string>();
  for (const [pos, node] of textblocksIn(state.doc, value.pending)) {
    for (const token of blockWordTokens(node, pos)) {
      if (!controller.verdicts.has(token.word) && !isSpellingWordAccepted(token.word)) words.add(token.word);
      if (words.size >= MAX_WORDS_PER_CHECK) return [...words];
    }
  }
  return [...words];
}

function resolvePending(doc: ProseMirrorNode, selection: Selection, value: SpellCheckState, controller: SpellCheckController): SpellCheckState {
  let { decorations, deferred } = value;
  const stillPending: Range[] = [];
  for (const [pos, node] of textblocksIn(doc, value.pending)) {
    const end = pos + node.nodeSize;
    const tokens = blockWordTokens(node, pos);
    if (tokens.some(token => !controller.verdicts.has(token.word) && !isSpellingWordAccepted(token.word))) {
      stillPending.push({ from: pos, to: end });
      continue;
    }
    decorations = decorations.remove(decorations.find(pos, end, spec => spec.spellKind === 'spelling'));
    const added: Decoration[] = [];
    for (const token of tokens) {
      if (isSpellingWordAccepted(token.word) || controller.verdicts.get(token.word) !== false) continue;
      const range = { from: token.start, to: token.end };
      if (caretInside(selection, range)) { deferred = range; continue; }
      added.push(spellingDecoration(token.start, token.end, token.word));
    }
    if (added.length) decorations = decorations.add(doc, added);
  }
  return { ...value, decorations, pending: stillPending, deferred };
}

function createSpellCheckPlugin(editor: Editor, options: SpellCheckOptions, controller: SpellCheckController) {
  const editable = () => editor.options.editable !== false && !editor.isDestroyed;
  return new Plugin<SpellCheckState>({
    key: spellCheckKey,
    state: {
      init: (_config, state) => ({
        enabled: options.enabled,
        decorations: DecorationSet.empty,
        pending: options.enabled ? [wholeDocument(state.doc)] : [],
        deferred: null,
      }),
      apply(tr, value, _oldState, newState) {
        const meta = tr.getMeta(spellCheckKey) as SpellCheckMeta | undefined;
        let enabled = value.enabled;
        let decorations = value.decorations.map(tr.mapping, tr.doc);
        let pending: readonly Range[] = value.pending;
        let deferred = value.deferred;
        if (tr.docChanged) {
          const mapRange = (range: Range) => ({ from: tr.mapping.map(range.from, -1), to: tr.mapping.map(range.to, 1) });
          pending = pending.map(mapRange);
          deferred = deferred ? mapRange(deferred) : null;
          if (enabled) pending = [...pending, ...changedRanges(tr)];
        }
        if (meta?.type === 'setEnabled' && meta.enabled !== enabled) {
          enabled = meta.enabled;
          decorations = DecorationSet.empty;
          pending = enabled ? [wholeDocument(tr.doc)] : [];
          deferred = null;
        } else if (meta?.type === 'rescanAll' && enabled) {
          pending = [wholeDocument(tr.doc)];
        } else if (meta?.type === 'halt') {
          pending = [];
        } else if (meta?.type === 'removeWord') {
          decorations = decorations.remove(decorations.find(undefined, undefined,
            spec => spec.spellKind === 'spelling' && spellingKey(String(spec.spellWord)) === meta.word));
        } else if (meta?.type === 'acceptedWordsChanged') {
          decorations = decorations.remove(decorations.find(undefined, undefined,
            spec => spec.spellKind === 'spelling' && isSpellingWordAccepted(String(spec.spellWord))));
          if (meta.rescan && enabled) pending = [wholeDocument(tr.doc)];
        }
        if (deferred && enabled && !caretInside(newState.selection, deferred)) {
          pending = [...pending, deferred];
          deferred = null;
        }
        let next: SpellCheckState = { enabled, decorations, pending: mergePending(tr.doc, pending), deferred };
        if (enabled && next.pending.length && (meta?.type === 'refresh' || meta?.type === 'acceptedWordsChanged' || tr.docChanged || tr.selectionSet)) {
          next = resolvePending(tr.doc, newState.selection, next, controller);
        }
        return next;
      },
    },
    props: {
      decorations(state) {
        const value = spellCheckKey.getState(state);
        return value?.enabled && editable() ? value.decorations : null;
      },
      attributes(state): Record<string, string> {
        // The browser's own checker would draw a second set of underlines.
        return spellCheckKey.getState(state)?.enabled ? { spellcheck: 'false' } : {};
      },
    },
    view(view) {
      let timer: ReturnType<typeof setTimeout> | null = null;
      let running = false;
      let destroyed = false;
      const dispatchMeta = (meta: SpellCheckMeta) => {
        if (destroyed || editor.isDestroyed) return;
        view.dispatch(view.state.tr.setMeta(spellCheckKey, meta).setMeta('addToHistory', false));
      };
      const needsWork = () => {
        const value = spellCheckKey.getState(view.state);
        return Boolean(value?.enabled && value.pending.length && editable() && !controller.failed);
      };
      const run = async () => {
        timer = null;
        if (destroyed || running || !needsWork()) return;
        const engine = controller.engine();
        if (!engine) { controller.failed = true; dispatchMeta({ type: 'halt' }); return; }
        const words = wordsAwaitingVerdict(view.state, controller);
        if (words.length) {
          running = true;
          try {
            const misspelled = await engine.check(words);
            for (const word of words) controller.verdicts.set(word, !misspelled.has(word));
          } catch {
            controller.failed = true;
            dispatchMeta({ type: 'halt' });
            return;
          } finally {
            running = false;
          }
        }
        dispatchMeta({ type: 'refresh' });
      };
      const schedule = () => {
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => { void run(); }, options.debounceMs);
      };
      const onIgnored = (word: string) => dispatchMeta({ type: 'removeWord', word });
      ignoreListeners.add(onIgnored);
      const onPersonalWordsChanged = (rescan: boolean) => dispatchMeta({ type: 'acceptedWordsChanged', rescan });
      personalListeners.add(onPersonalWordsChanged);
      if (needsWork()) schedule();
      return {
        update(current, previous) {
          if (running || !needsWork()) return;
          if (current.state.doc !== previous.doc || !timer) schedule();
        },
        destroy() {
          destroyed = true;
          if (timer) clearTimeout(timer);
          ignoreListeners.delete(onIgnored);
          personalListeners.delete(onPersonalWordsChanged);
        },
      };
    },
  });
}

export const DocumentSpellCheck = Extension.create<SpellCheckOptions, SpellCheckStorage>({
  name: SPELL_CHECK_EXTENSION,

  addOptions() {
    return {
      enabled: true,
      engine: getBrowserSpellCheckEngine,
      debounceMs: SPELL_CHECK_DEFAULT_DEBOUNCE_MS,
      autocorrect: true,
    };
  },

  addStorage() {
    return { controller: { engine: this.options.engine, verdicts: new Map(), failed: false } };
  },

  addCommands() {
    return {
      setSpellCheckEnabled: (enabled: boolean) => ({ tr, dispatch }) => {
        if (dispatch) {
          if (enabled) this.storage.controller.failed = false;
          tr.setMeta(spellCheckKey, { type: 'setEnabled', enabled } satisfies SpellCheckMeta).setMeta('addToHistory', false);
        }
        return true;
      },
      refreshSpelling: () => ({ tr, dispatch }) => {
        if (dispatch) {
          this.storage.controller.verdicts.clear();
          this.storage.controller.failed = false;
          tr.setMeta(spellCheckKey, { type: 'rescanAll' } satisfies SpellCheckMeta).setMeta('addToHistory', false);
        }
        return true;
      },
      replaceMisspelling: (issue, replacement) => ({ tr, state, dispatch }) => {
        const text = replacement.trim();
        const size = state.doc.content.size;
        if (!text || issue.from < 0 || issue.to > size || issue.to <= issue.from) return false;
        if (normaliseWord(state.doc.textBetween(issue.from, issue.to)) !== normaliseWord(issue.word)) return false;
        if (dispatch) {
          tr.insertText(text, issue.from, issue.to);
          tr.setSelection(TextSelection.create(tr.doc, issue.from + text.length));
        }
        return true;
      },
    };
  },

  addProseMirrorPlugins() {
    const controller = this.storage.controller;
    const plugins: Plugin[] = [createSpellCheckPlugin(this.editor, this.options, controller)];
    const editor = this.editor;
    if (this.options.autocorrect) {
      plugins.push(createAutocorrectPlugin({
        editable: () => editor.options.editable !== false && !editor.isDestroyed,
        isEnabled: state => Boolean(spellCheckKey.getState(state)?.enabled),
        isWordAccepted: isSpellingWordAccepted,
        verdict: word => controller.verdicts.get(word),
      }));
    }
    return plugins;
  },
});

/** Flagged words currently shown, in document order. */
export function spellingIssues(state: EditorState): SpellingIssue[] {
  const value = spellCheckKey.getState(state);
  if (!value?.enabled) return [];
  return value.decorations.find(undefined, undefined, spec => spec.spellKind === 'spelling')
    .map(decoration => ({ from: decoration.from, to: decoration.to, word: String(decoration.spec.spellWord) }))
    .sort((left, right) => left.from - right.from);
}

/** The flagged word at or touching a document position, if any. */
export function spellingIssueAt(state: EditorState, position: number): SpellingIssue | null {
  const value = spellCheckKey.getState(state);
  if (!value?.enabled) return null;
  const found = value.decorations.find(position, position, spec => spec.spellKind === 'spelling')
    .find(decoration => decoration.from <= position && position <= decoration.to);
  return found ? { from: found.from, to: found.to, word: String(found.spec.spellWord) } : null;
}

function controllerOf(editor: Editor): SpellCheckController | null {
  const storage = (editor.storage as unknown as Record<string, unknown>)[SPELL_CHECK_EXTENSION] as Partial<SpellCheckStorage> | undefined;
  return storage?.controller ?? null;
}

/** Up to five corrections for a flagged word, from the dictionary worker. */
export async function getSpellingSuggestions(editor: Editor, word: string): Promise<string[]> {
  const engine = controllerOf(editor)?.engine();
  return engine ? engine.suggest(word) : [];
}
